import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { appendDurationForTunnelPurchaseFlow, DEFAULT_COVER_IMAGE, getProductTerm, getTunnelProductTerm, getTunnelProductTitleVerbose } from '../../common/utils/product-util';
import { ProductCardData } from '../../features/product-card/product-card-display';
import { getPriceWithCurrency, getProductEndAccessDate } from '../../common/utils/cart-util';
import "./product-verbiage";
import { BaseComponent } from '../../features/base-component/base-component';
import { isVirtualLabsItem } from '../virtual-labs/utils/virtual-labs.util';
import { aleksSharpenCheckoutCard } from './utils/product-card-metadata.util';

/**
 * A static element to display product information.
 *
 */
@customElement('mhecomm-product-info-display')
export class ProductInformationDisplay extends BaseComponent {
  /**
   * A JSON String in the format of 'ProductCardData'.
   */
  @property({ type: Object })
  product?: ProductCardData;

  renderComp() {
    if (!this.product) {
      return nothing;
    }
    const title = getTunnelProductTitleVerbose(this.product?.title, this.product);
    const term = getTunnelProductTerm(this.product?.term);
    const productTerm = getProductTerm({
      term,
      itemType: this.product?.itemType,
      offerType: this.product?.offerType,
    });

    return html` <section>
          <h2 class="fw-semibold">Product Information</h2>
          <hr class="hr-primary mt-0" aria-hidden="true"/>
          <div class="row mb-3">
            <div class="col-3">
              <img
                data-automation-id="product-img-${this.product?.isbn}"
                alt=""
                class="img-fluid"
                src=${this.product?.image ?? this.loadDefaultImage()}
              />
            </div>
            ${this.product?.isAleksSharpenCompanionBundle
              ? html`${aleksSharpenCheckoutCard(this.product)}`
              : html`
                <div class="col-9 flex-row d-flex">
                  <div>
                    <h3 id="product-info-title" data-automation-id="product-info-title" class="fs-6 fw-semibold mt-1 me-2">
                      ${this.product.isVirtual
                        ? appendDurationForTunnelPurchaseFlow({
                            duration: this.product?.duration,
                            durationPeriod: this.product?.durationPeriod,
                            title,
                            term
                          })
                        : title
                      }
                    </h3>
                    ${this.isLongCurrency() ? html`<span class="fw-bold fs-large d-block" id="product-info-total-price" data-automation-id="product-info-total-price">${getPriceWithCurrency(this.product?.currencyCode, this.product?.totalPrice!)}</span>` : nothing}
                    ${productTerm
                      ? html` <span class="fw-bold">Access Until:</span> ${getProductEndAccessDate(productTerm)}`
                      : nothing}
                  </div>
                  ${!this.isLongCurrency() ? html`
                    <div class="d-inline-flex justify-content-end">
                        <span class="fw-bold fs-large" id="product-info-total-price" data-automation-id="product-info-total-price">${getPriceWithCurrency(this.product?.currencyCode, this.product?.totalPrice!)}</span>
                    </div>` :nothing}
                </div>
              `
            }
          </div>
          ${this.getProductVerbiage(this.product)}
        </section>`;
  }

  getProductVerbiage(product: ProductCardData) {
    return html`<mhecomm-product-verbiage
      .application=${product?.application}
      .flow="${product.flow}"
      .looseLeafAvailable=${product?.looseLeafAvailable}
      .hasEbook=${product?.hasEbook}
      .isConnectSharpenBundle=${product?.isConnectSharpenBundle}
      .isSharpenCompanion=${product?.isSharpenCompanion}
      .isSharpenAdvantage=${product?.isSharpenAdvantage}
      .isVirtualLabs=${isVirtualLabsItem(product)}
      .isAleksSharpenCompanionBundle=${product?.isAleksSharpenCompanionBundle}
    >
    </mhecomm-product-verbiage>`;
  }

  loadDefaultImage() {
    return DEFAULT_COVER_IMAGE;
  }

  isLongCurrency() {
    return this.product && typeof this.product?.totalPrice === "number" && this.product?.totalPrice > 9999.99;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-product-info-display': ProductInformationDisplay;
  }
}
